import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Eye, EyeOff, CheckCircle, XCircle, Server } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres.");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password/${token}`, {
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Lien invalide ou expire.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
            <Server className="text-cyan-400" size={28} />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ServeurOnline</h1>
            <p className="text-sm text-slate-500">Backup Monitoring SaaS</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800/60 bg-[#0D111A]/70 backdrop-blur-xl p-8">
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h2 className="font-heading text-xl font-bold text-white">Mot de passe modifie</h2>
              <p className="text-slate-400 text-sm">
                Votre mot de passe a ete reinitialise avec succes.
              </p>
              <Link to="/login">
                <Button className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">
                  Se connecter
                </Button>
              </Link>
            </div>
          ) : error ? (
            <div className="text-center space-y-4" data-testid="reset-password-error">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <XCircle className="text-red-400" size={32} />
              </div>
              <h2 className="font-heading text-xl font-bold text-white">Lien invalide</h2>
              <p className="text-slate-400 text-sm">{error}</p>
              <Link to="/forgot-password">
                <Button variant="secondary" className="mt-4 bg-slate-800 hover:bg-slate-700 text-white">
                  Refaire une demande
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl font-bold text-white mb-2">
                Nouveau mot de passe
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                Choisissez un nouveau mot de passe pour votre compte.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 6 caracteres"
                      required
                      minLength={6}
                      className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500 pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirmer le mot de passe</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retapez le mot de passe"
                    required
                    minLength={6}
                    className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                    data-testid="reset-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-5 rounded-xl"
                  data-testid="reset-submit-btn"
                >
                  {isLoading ? "Reinitialisation..." : "Reinitialiser le mot de passe"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
